/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import cloneDeep from 'lodash.clonedeep';
import {consolidatedAssetUpdate, getAsset} from '@ion/core/src/services/Connect';
import {AccountLink, Asset, CRUDPermissions, DeviceFile} from '@ion/core/src/types/entities';
import {DropDownInfiniteScrollProps} from '@ion/dls/src/components/menus/Dropdown';
import {
  AssetAssociation,
  AssetEditor,
  AssetEditorShowDefaultProps,
  AssetTreeRefresh
} from '@ion/dls/src/editors/AssetEditor';
import {useAuthenticatedUser} from '../users/useAuthenticatedUser';
import {
  auditAdminAsset,
  editAdminAsset,
  editAsset
} from './state';

export interface EditAssetEditorProps extends AssetAssociation,
  AssetEditorShowDefaultProps,
  DropDownInfiniteScrollProps,
  AssetTreeRefresh {
  onCloseDialog?: (event: SyntheticEvent<{}, Event>) => void;
  onEdit?: Function;
  onAudit?: Function;
  globalGroup: AccountLink;
  isCtcAdmin?: boolean;
  isViewDialog?: boolean;
  includeGroupProperties?: boolean;
  includeDeviceGridCheckboxes?: boolean;
  includeAssetGridCheckboxes?: boolean;
  assetsPermissions?: CRUDPermissions;
  isMultiSelectDevice?: boolean;
};
/**
 * EditAssetEditor
 *
 */
export const EditAssetEditor = ({
  onCloseDialog,
  onEdit,
  onAudit,
  globalGroup,
  assetsPermissions,
  isCtcAdmin = false,
  isViewDialog = false,
  includeAssetAssociation = false,
  includeFilteredAssetAssociation = false,
  isReadOnly = false,
  includeGroupProperties = true,
  includeDeviceGridCheckboxes = false,
  includeAssetGridCheckboxes = false,
  isInfiniteScroll = false,
  showTreeRefresh = false,
  isMultiSelectDevice = false
}: EditAssetEditorProps) => {
  const dispatch                                    = useDispatch();
  const {account}                                   = useAuthenticatedUser();
  const [clearForm, setClearForm]                   = useState(false);
  const [isDeviceAssociated, setIsDeviceAssociated] = useState<boolean>(false);
  const {
    selected: selectedAsset,
    result: editedAsset,
    loading,
    error
  } = useSelector(({adminAssets: {edit}}) => edit);
  const [oldAssetData, setOldAssetData] = useState(selectedAsset);
  const updateAssetAction = {
    isError : true,
    showTreeRefresh
  };
  const updateAsset = {
    isError : false,
    showTreeRefresh
  };

  useEffect(() => {
    const assetData = cloneDeep(oldAssetData);
    assetData.account = globalGroup || account;
    assetData.previousAccount = globalGroup || account;
    (async() => {
      if (error) {
        const refreshedAsset = await getAsset(assetData.id);
        assetData.version = refreshedAsset?.version;
        await consolidatedAssetUpdate(assetData);
      }
    })();
  }, [error]);

  useEffect(() => {
    if (editedAsset) {
      setClearForm(true);
      dispatch(editAdminAsset(null, false, isViewDialog));
    }
  }, [editedAsset]);

  async function handleSubmit(asset: Asset) {
    if (!isViewDialog) {
      const {device} = asset;
      if (device) {
        asset.device = device.map(
          (item: DeviceFile) => item._self ? item._self : item);
      }
      if (error) {
        dispatch(editAsset(asset, undefined, updateAssetAction));
      } else {
        dispatch(editAsset(asset, undefined, updateAsset));
      }
    } else {
      dispatch(auditAdminAsset(asset, true));
    }
  }

  const onClose = useCallback(() => {
    dispatch(editAdminAsset(null, false, false));
  }, []);

  return (
    <AssetEditor open
      isReadOnly={!isReadOnly}
      isViewDialog={isViewDialog}
      account={account}
      defaultAccount={globalGroup}
      asset={selectedAsset}
      onSubmit={handleSubmit}
      onEdit={onEdit}
      onAudit={onAudit}
      onDeviceAssociated={setIsDeviceAssociated}
      isDeviceAssociated={isDeviceAssociated}
      onCloseDialog={onCloseDialog || onClose}
      clear={clearForm}
      loading={loading}
      includeAssetAssociation={includeAssetAssociation}
      includeFilteredAssetAssociation={!includeFilteredAssetAssociation}
      isCtcAdmin={isCtcAdmin}
      assetsPermissions={assetsPermissions}
      includeGroupProperties={includeGroupProperties}
      includeDeviceGridCheckboxes={includeDeviceGridCheckboxes}
      includeAssetGridCheckboxes={includeAssetGridCheckboxes}
      isInfiniteScroll={!isInfiniteScroll}
      isMultiSelectDevice={isMultiSelectDevice} />
  );
};

export default EditAssetEditor;
